import { useCallback, useEffect, useState } from 'react';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { useAuth } from '../../../hooks/useAuth';
import { useEnv } from '../../../context/env-context';
import { makeRequest } from '../../../utils/makeRequest';
import { toast } from 'react-toastify';
import { Spinner } from '../../../components/Spinner';
import DashboardPaymentMethods from './advances/DashboardPaymentMethods';
import DashboardForecast from './advances/DashboardForecast';
import DashboardAdvancesOffers from './advances/DashboardAdvancesOffers';

function DashboardAdvances({ aggregators, getAggregators }) {
  const { getAccessTokenSilently } = useAuth();
  const { apiOriginConsumer, apiOriginMonitor, apiOriginFunding } = useEnv();

  const [defaultDashboard, setDefaultDashboard] = useState(false);

  useEffect(() => {
    setDefaultDashboard(localStorage.getItem('dashboard') === 'advances');
  }, []);

  const [spinner, setSpinner] = useState(false);
  const [aggregatorId, setAggregatorId] = useState(null);

  const [paymentMethods, setPaymentMethods] = useState(null);
  const [consumers, setConsumers] = useState({ active: null, billable: null });

  const [fundingSummary, setFundingSummary] = useState(null);
  const [activeOffers, setActiveOffers] = useState(null);
  const [dwollaBalance, setDwollaBalance] = useState(null);

  const advancesGetRequest = useCallback(
    async ({ url, callback, customErrorMessage }) => {
      const token = await getAccessTokenSilently();

      if (!token) {
        return null;
      }

      const config = { token, url, method: 'GET', params: { aggregatorId } };

      try {
        const response = await makeRequest(config);

        if (callback) {
          callback(response);
        }

        return response;
      } catch (error) {
        toast.error(`${customErrorMessage} ${error.message}`);

        return null;
      }
    },
    [aggregatorId, getAccessTokenSilently],
  );

  const formatAdvances = useCallback(obj => {
    const newObj = { ...obj };

    Object.keys(newObj).forEach(item => {
      if (Array.isArray(newObj[item]) && item !== 'agingData') {
        newObj[item] = newObj[item].map(v => Number(v));
      } else if (
        item !== 'CurrentMonth' &&
        item !== 'PreviousMonth' &&
        item !== 'CurrentMonthCardFailures' &&
        item !== 'CurrentMonthCardFailuresConsumers' &&
        item !== 'CurrentMonthCardFailuresConsumersTotal' &&
        item !== 'CurrentMonthCardTransfers' &&
        item !== 'CurrentMonthCardTransfersConsumers' &&
        item !== 'TransferFailuresCurrentMonth' &&
        item !== 'TransferFailuresPreviousMonth' &&
        item !== 'TransferFees_sum' &&
        item !== 'collectionForecast' &&
        item !== 'agingData'
      ) {
        newObj[item] = Number(newObj[item]).toLocaleString('en-US', {
          maximumFractionDigits: 0,
        });
      } else if (item === 'CurrentMonth' || item === 'PreviousMonth') {
        newObj[item] = String(newObj[item]);
      } else if (
        item === 'CurrentMonthCardFailures' ||
        item === 'CurrentMonthCardFailuresConsumers'
      ) {
        newObj[item] = { ...newObj[item] };
      } else if (
        item === 'CurrentMonthCardTransfers' ||
        item === 'CurrentMonthCardFailuresConsumersTotal' ||
        item === 'CurrentMonthCardTransfersConsumers'
      ) {
        newObj[item] = Number(newObj[item]);
      } else if (
        item === 'TransferFailuresCurrentMonth' ||
        item === 'TransferFailuresPreviousMonth'
      ) {
        if (newObj[item] === null) {
          newObj[item] = 'N/A';
        } else {
          newObj[item] = `${(Number(newObj[item]) * 100).toLocaleString('en-US', {
            maximumFractionDigits: 1,
          })}%`;
        }
      } else if (item === 'TransferFees_sum') {
        if (newObj[item] === null) {
          newObj[item] = 'N/A';
        } else {
          newObj[item] = `${Number(newObj[item]).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`;
        }
      } else {
        Object.keys(obj.collectionForecast).forEach(i => {
          formatAdvances(obj.collectionForecast[i]);
        });
      }
    });

    return newObj;
  }, []);

  const getAllData = useCallback(async () => {
    setSpinner(true);
    await Promise.all([
      getAggregators(),
      advancesGetRequest({
        url: `${apiOriginConsumer}/get_payment_methods_by_aggregator/v2`,
        callback: res => setPaymentMethods(res),
        customErrorMessage: 'Failed getting payment methods:',
      }),
      advancesGetRequest({
        url: `${apiOriginConsumer}/get_counts_by_aggregator`,
        callback: res =>
          setConsumers({ active: res.byStatus.ACTIVE, billable: res.billableConsumers }),
        customErrorMessage: 'Failed getting consumers:',
      }),
      advancesGetRequest({
        url: `${apiOriginFunding}/get_funding_summary_by_aggregator`,
        callback: res => setFundingSummary(formatAdvances(res)),
        customErrorMessage: 'Failed getting advance summary:',
      }),
      advancesGetRequest({
        url: `${apiOriginMonitor}/get_active_offers_by_aggregator`,
        callback: res => setActiveOffers(formatAdvances(res)),
        customErrorMessage: 'Failed getting active offers:',
      }),
      advancesGetRequest({
        url: `${apiOriginFunding}/get_dwolla_balance`,
        callback: res => setDwollaBalance(res),
        customErrorMessage: 'Failed getting dwolla balance:',
      }),
    ]);
    setSpinner(false);
  }, [
    getAggregators,
    advancesGetRequest,
    formatAdvances,
    apiOriginConsumer,
    apiOriginFunding,
    apiOriginMonitor,
  ]);

  useEffect(() => {
    getAllData();
  }, [getAllData]);

  return (
    <Spinner visible={spinner}>
      <Row className="align-items-start">
        <Col md={12} className="mb-3 d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <Form.Select
              className="bo-filter w-100"
              value={aggregatorId ?? ''}
              onChange={e => setAggregatorId(e.target.value)}
            >
              <option value="">All Standard Aggregators</option>
              {aggregators.map(agg => (
                <option key={agg.id} value={agg.id}>
                  {agg.name}
                </option>
              ))}
            </Form.Select>
            <Button className="bo-button-inline-link" onClick={getAllData}>
              Refresh
            </Button>
          </div>
          <div className="d-flex">
            <Form.Check
              checked={defaultDashboard}
              onChange={() => {
                setDefaultDashboard(prev => !prev);
                if (localStorage.getItem('dashboard') === 'advances') {
                  localStorage.removeItem('dashboard');
                } else {
                  localStorage.setItem('dashboard', 'advances');
                }
              }}
            />
            <Form.Label className="p-0 m-0">Make this my default dashboard</Form.Label>
          </div>
        </Col>
      </Row>
      <Row className="align-items-start">
        <Col md={6}>
          <DashboardPaymentMethods paymentMethods={paymentMethods} consumers={consumers} />
          <DashboardForecast collectionForecast={fundingSummary?.collectionForecast} />
        </Col>
        <Col md={6}>
          <DashboardAdvancesOffers
            fundingSummary={fundingSummary}
            activeOffers={activeOffers}
            dwollaBalance={dwollaBalance}
          />
        </Col>
      </Row>
    </Spinner>
  );
}

export default DashboardAdvances;
